import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, } from '@angular/core';
import { combineLatest, EMPTY, forkJoin, map, of, Subscription, distinctUntilChanged } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { OfferPromo, PromoDomain, OfferPromoService, OfferGroupDomain, Offer } from '../..';
import { SelectionModel } from '@angular/cdk/collections';
import { Status } from '../../models/offer-promo-status';
import { EventWorkflowService } from '../../services/event-workflow.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CommentsComponent } from 'pr1-ui-components';
import { MatTableDataSource } from '@angular/material/table';
import { DOCUMENT } from '@angular/common';
import { OfferCopy } from '../../models/offer-copy.model';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowStage } from '../../models/workflow-stage.model';
import { ListViewStateService } from '../../services/list-view-state.service';
import {
  buildOfferDisplayRows,
  commentsAnchorIdForOffer,
  getNonBaseVariants,
  isExpandableOffer,
  OfferDisplayRow,
  trackOfferDisplayRow,
} from '../../configs/offer-variant-rows.config';

const log = new Logger('OfferListTableComponent');

@Component({
  selector: 'app-offer-list-table',
  templateUrl: './offer-list-table.component.html',
  styleUrls: ['./offer-list-table.component.scss'],
})
export class OfferListTableComponent<TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>> implements OnInit, AfterViewInit, OnDestroy {
  @Input() set readOnly(val: boolean) {
    this.readOnlyMode = val;
    this.offerpromoService.setOfferDetailsReadOnly(val);
  }

  @Input() set variantViewMode(val: 'compressed' | 'expanded') {
    const prev = this._variantViewMode;
    this._variantViewMode = val;
    if (prev === val) {
      return;
    }
    this.rebuildDisplayRows();
    if (val === 'expanded') {
      this.startExpandedVariantLoads();
    }
  }

  get variantViewMode(): 'compressed' | 'expanded' {
    return this._variantViewMode;
  }

  private _variantViewMode: 'compressed' | 'expanded' = 'compressed';

  @Output() contentRendered = new EventEmitter<void>();
  @Output() viewSelected = new EventEmitter<void>();
  Status: typeof Status = Status;
  PreviewMode = PreviewMode;
  viewModel$ = combineLatest([
    this.offerpromoService.getAllEventOfferTypesByEventId$,
    this.offerpromoService.workflowStages$
  ]).pipe(
    map(([eventOfferTypes, workflowStages]) => {
      this.workflowStages = workflowStages;
      this.eventOfferTypes = new MatTableDataSource(eventOfferTypes);
      this.syncSelectionWithDataSource();
      this.rebuildDisplayRows();
      if (this._variantViewMode === 'expanded') {
        this.startExpandedVariantLoads();
      }

      requestAnimationFrame(() => {
        this.contentRendered.emit();
      });
      return { eventOfferTypes };
    })
  );
  workflowStages: WorkflowStage[] = [];
  eventOfferTypes: MatTableDataSource<OfferGroupDomain<Offer>> = new MatTableDataSource();
  /** Flattened rows for mat-table when variant view is expanded (base / skeleton / variant). */
  tableVariantDataSource = new MatTableDataSource<OfferDisplayRow>([]);
  displayRows: OfferDisplayRow[] = [];

  selection = new SelectionModel<OfferGroupDomain<Offer>>(true, [], true, (o1, o2) => o1.DomainId === o2.DomainId);
  readOnlyMode = true;
  isCommentDialogOpened = false;
  private subscriptions: Subscription[] = [];
  displayedColumns = ['select', 'Image', 'Name', 'Headline', 'BodyCopy', 'EventType', 'Status', 'Tags', 'VersionCodes', 'Version', 'Rank', 'Comments', 'Versions', 'PageNumber', 'Position', 'Actions',];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private mediaObserver: MediaObserver,
    public utilityService: UtilityService,
    private ewService: EventWorkflowService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private listViewState: ListViewStateService,
  ) { }

  ngOnInit(): void {
    this.detectViewportSizeChange();
    this.offerpromoService.commentDialogBoxStatus$.subscribe((status: boolean) => { this.isCommentDialogOpened = status });

    this.subscriptions.push(
      this.listViewState.listViewState$
        .pipe(
          map(s => (s.selectedRecords ?? []).map(r => String(r?.DomainId ?? '')).sort().join('|')),
          distinctUntilChanged(),
        )
        .subscribe(() => this.syncSelectionWithDataSource())
    );
  }

  get selectedCount(): number {
    return this.listViewState.listViewStateValue.selectedRecords?.length ?? 0;
  }

  onViewSelected(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.viewSelected.emit();
  }

  ngAfterViewInit() {
    if (this.ewService.readOnlyMode != null) {
      this.readOnlyMode = this.ewService.readOnlyMode;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private rebuildDisplayRows(): void {
    this.displayRows = buildOfferDisplayRows(this.eventOfferTypes?.data ?? [], this._variantViewMode);
    this.tableVariantDataSource.data = this.displayRows;
  }

  private startExpandedVariantLoads(): void {
    if (this._variantViewMode !== 'expanded') {
      return;
    }
    const data = this.eventOfferTypes?.data ?? [];
    let markedLoading = false;

    for (const offer of data) {
      if (!isExpandableOffer(offer)) {
        continue;
      }
      const nonBase = getNonBaseVariants(offer);
      if (nonBase.length === 0) {
        continue;
      }
      const cached = offer['_expandedVariantCards'] as OfferGroupDomain<Offer>[] | undefined;
      if (cached?.length) {
        continue;
      }
      if (offer['_expandedVariantsLoading']) {
        continue;
      }

      offer['_expandedVariantsLoading'] = true;
      markedLoading = true;

      const et = offer.EventOfferType!;
      const requests = nonBase.map((v) =>
        this.offerpromoService.getVariantDetails(v.Id!, et).pipe(
          catchError((error) => {
            log.error('Error fetching variant details (GetPreview)', error);
            return of(null);
          }),
          map((response) => {
            if (!response) {
              return null;
            }
            const card = JSON.parse(JSON.stringify(offer)) as OfferGroupDomain<Offer>;
            card['_isVariantCard'] = true;
            card['_variantId'] = v.Id;
            if (v.Id && card.Detail) {
              card.Detail.Id = v.Id;
            }
            Object.keys(response).forEach((key) => {
              if (key !== 'Variants' && key !== 'VariantsCount') {
                card[key] = (response as any)[key];
              }
            });
            return card;
          }),
        ),
      );

      forkJoin(requests).subscribe((results) => {
        offer['_expandedVariantCards'] = results.filter(Boolean) as OfferGroupDomain<Offer>[];
        offer['_expandedVariantsLoading'] = false;
        this.rebuildDisplayRows();
        this.cdRef.markForCheck();
      });
    }

    if (markedLoading) {
      this.rebuildDisplayRows();
      this.cdRef.markForCheck();
    }
  }

  trackDisplayRow(index: number, row: OfferDisplayRow): string {
    return trackOfferDisplayRow(index, row);
  }

  detectViewportSizeChange() {
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.subscriptions.push(this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        )
      )
      .subscribe((change) => {
        if (change.some((x) => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Name', 'Actions'];
        } else if (change.some((x) => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Name', 'Headline', 'Actions'];
        } else {
          this.displayedColumns = ['select', 'Image', 'Name', 'Headline', 'BodyCopy', 'EventType', 'Status', 'Tags', 'VersionCodes', 'Version', 'Rank', 'Comments', 'Versions', 'PageNumber', 'Position', 'Actions',];
        }
      }));
  }

  fetchVariantDetails(variantId: string, offerType: OfferGroupDomain<Offer>): void {
    if (variantId && offerType.EventOfferType && offerType.EventOfferType !== 'OFFERGROUP') {
      this.offerpromoService.getVariantDetails(variantId, offerType.EventOfferType)
        .pipe(
          catchError(error => {
            log.error('Error fetching variant details', error);
            return [];
          })
        )
        .subscribe(response => {
          // Update offerType properties with the response, excluding Variants and VariantsCount
          if (!response) {
            return;
          }
          Object.keys(response).forEach(key => {
            if (key !== 'Variants' && key !== 'VariantsCount') {
              offerType[key] = response[key];
            }
          });
        });
    }
  }

  onVariantChange(variantId: string, offerType: OfferGroupDomain<Offer>): void {
    this.fetchVariantDetails(variantId, offerType);
  }

  onSort(sortState: Sort): void {
    this.selection.clear();
    this.offerpromoService.sort(sortState);
  }

  clearSelection() {
    this.selection.clear();
    this.listViewState.clearSelection();
  }

  syncSelectionWithDataSource() {
    const selectedRecords = this.listViewState.listViewStateValue.selectedRecords;
    this.selection.clear();
    this.eventOfferTypes.data.forEach(offer => {
      const isSelected = selectedRecords.some(record => record.DomainId === offer.DomainId);

      if (offer.DomainId && isSelected) {
        this.selection.select(offer);
      }
    });
  }

  updateStatus(status: Status, row: OfferGroupDomain<Offer>) {
    let selectedRecordsDetails: OfferGroupDomain<Offer>[] = [JSON.parse(JSON.stringify(row))];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<OfferGroupDomain<Offer>>, status: Status) {
    let details: Array<OfferCopy> = [];
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      details = selectedRecordsDetails.map((record: OfferGroupDomain<Offer>) => {

        let copy: OfferCopy = {
          Id: record.Detail.Id,
          Label: record.EventOfferType,
          Status: status as unknown as string
        };

        return copy;
      });
      this.offerpromoService.updateStatus(details)
        .pipe(
          catchError(error => {
            log.error('Error updating status', error);
            return [];
          })
        )
        .subscribe({
          next: () => {
            this.offerpromoService.reload();
            this.clearSelection();
            this.offerpromoService.updateRecordStatus(new Date());
          },
        });
    }
  }

  canEdit(): boolean {
    return !this.readOnlyMode;
  }

  commentsAnchorId(offer: OfferGroupDomain<Offer>): string {
    return commentsAnchorIdForOffer(offer);
  }

  openDialog($event: any, offer: OfferGroupDomain<Offer>) {
    if (!this.isCommentDialogOpened) {
      const commentElementStyle = offer.DomainId ? this.document.getElementById(offer.DomainId) as HTMLElement : null;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = false;
      dialogConfig.data = {
        headline: offer.Detail.Headline,
        channel: offer.EventOfferType,
        parentId: offer.Detail.Id,
        clientKey: offer.Detail.ClientKey,
        bodyCopy: offer.Detail.BodyCopy,
        showOfferCheckbox: true,
        workflowStage: this.workflowStages.length ? this.workflowStages[0] : null
      };
      const confirmDialog = this.dialog.open(CommentsComponent, dialogConfig);

      try {
        const instance = confirmDialog.componentInstance;
        instance.updateToOfferEmitter.subscribe(() => {
          this.updateStatus(Status.PENDING, offer);
        });
      } catch (error) {
        console.error('Error accessing instance:', error);
      }

      confirmDialog.afterOpened().subscribe(() => {
        if (commentElementStyle) {
          commentElementStyle.style.color = "blue";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(true);
        }
      });
      confirmDialog.afterClosed().subscribe((data) => {
        if (data > 0) {
          Object(offer).CommentsCount = data;
        }
        if (commentElementStyle) {
          commentElementStyle.style.color = "black";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(false);
        }
      });
    }
  }

  updateReadonlyMode(val: boolean) {
    this.readOnlyMode = val;
    this.offerpromoService.setOfferDetailsReadOnly(val);
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png'; // Path to your default image
  }

  isAllSelected(): boolean {
    return this.eventOfferTypes.data.every(offer => this.selection.isSelected(offer));
  }

  toggleSelect(checked: boolean, offer: OfferGroupDomain<Offer>) {
    if (checked) {
      this.selection.select(offer);
    } else {
      this.selection.deselect(offer);
    }

    if (offer.DomainId) {
      this.listViewState.toggleSelection(offer, 'DomainId');
    }
  }

  toggleSelectAll(checked: boolean): void {
    this.eventOfferTypes.data.forEach(offer => {
      if (!offer.DomainId) return;

      const isAlreadySelected = this.listViewState.isSelected(offer.DomainId, 'DomainId');

      if (checked && !isAlreadySelected) {
        this.selection.select(offer);
        this.listViewState.toggleSelection(offer, 'DomainId');
      } else if (!checked && isAlreadySelected) {
        this.selection.deselect(offer);
        this.listViewState.toggleSelection(offer, 'DomainId');
      }
    });
  }

  isSomeSelectedOnPage(): boolean {
    return this.eventOfferTypes.data.some(offer => this.selection.isSelected(offer));
  }

  getStatusClass(offer: OfferGroupDomain<Offer>): string {
    const status = offer?.Detail?.Status;
    return status === '0' ? 'statusPENDING' : 'status' + status;
  }

  getStatusText(offer: OfferGroupDomain<Offer>): string {
    const status = offer?.Detail?.Status;
    return status === '0' ? 'PENDING' : status || '';
  }

  trackByFn(index: number, item: OfferGroupDomain<Offer>): number {
    return item.DomainId ? parseInt(item.DomainId, 10) : 0;
  }
}
